import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  message,
  Tooltip,
  Select,
  DatePicker,
  Popover,
} from 'antd'
import dayjs from 'dayjs'
import {
  DollarOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  KeyOutlined,
  SearchOutlined,
  StopOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useEffect, useState } from 'react'
import {
  useDownloadAgreement,
  useExportOrders,
  useGetAgreementEmbedUrl,
  useOrderList,
  useRefundOrder,
  useUnsignedContractUsers,
} from '../hooks/useOrder'
import {
  useSendSignReminderByBatch,
  useSendSignReminderByOrderIds,
  useSmsDetails,
} from '../hooks/useSms'
import { getPagination } from '../components/TablePagination'
import { OrderService } from '../services/orderService'
import { OrderDecryptor } from '../components/OrderDecryptor'
import { useCreateBlacklist } from '../hooks/useBlacklist'
import { useAllCourses } from '../hooks/useCourses'
import { useAllRegions } from '../hooks/useRegions'
import { usePositions } from '../hooks/usePositions'
import { useClasses } from '../hooks/useClasses'
import { useBatches } from '../hooks/useBatches'
import { formatPositionCodeName } from '../utils/positionFormatter'
import { SMS_STATUS_MAP, type SmsDetailsQueryParams, type SmsRecord } from '../services/smsService'
import type { MenuProps } from 'antd'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import type { ColumnsType } from 'antd/es/table'
import type {
  Order,
  OrderQueryParams,
  UnsignedContractUser,
} from '../services/orderService'
import { formatDateTime } from '../utils/TimeFormater'
import { getSmsErrorMessage, getSmsErrorSolution, SUCCESS } from '../constants/smsErrorCodes'

export const Route = createFileRoute('/order')({
  component: OrderPage,
})

export interface Blacklist {
  id: number
  name: string
  phone: string
  createdAt: Date
}
// 枚举定义
const ORDER_STATUS_OPTIONS = [
  { text: '待支付', value: 'PENDING_PAYMENT' },
  { text: '待签约', value: 'PENDING_CONTRACT' },
  { text: '待选择住宿', value: 'PENDING_ACCOMMODATION' },
  { text: '待分配班级', value: 'PENDING_CLASS_ALLOCATION' },
  { text: '已分配班级', value: 'CLASS_ALLOCATED' },
  { text: '已取消', value: 'CANCELLED' },
  { text: '已退款', value: 'REFUNDED' },
]

const AGREEMENT_STATUS_OPTIONS = [
  { text: '待签署', value: 'PENDING_SIGN' },
  { text: '已签署', value: 'SIGNED' },
]

// 常量定义
const TABLE_SCROLL_HEIGHT = 'calc(80vh - 140px)'
const TABLE_SCROLL_X = 1200
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

// 短信详情表格列定义
const smsDetailsColumns: ColumnsType<SmsRecord> = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
  { title: '手机号', dataIndex: 'phoneNumber', key: 'phoneNumber', width: 120 },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 80,
    render: (status: number) => {
      const statusInfo = SMS_STATUS_MAP[status] || {
        color: 'default',
        text: '未知',
      }
      return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
    },
  },
  {
    title: '发送时间',
    dataIndex: 'sendDate',
    key: 'sendDate',
    width: 150,
    render: formatDateTime,
  },
  {
    title: '接收时间',
    dataIndex: 'receiveDate',
    key: 'receiveDate',
    width: 150,
    render: formatDateTime,
  },
  {
    title: '内容',
    dataIndex: 'content',
    key: 'content',
    width: 300,
    ellipsis: true,
    render: (text: string) => (
      <Popover
        content={<div style={{ maxWidth: '300px', wordBreak: 'break-all' }}>{text}</div>}
        title="短信内容"
        trigger="click"
        placement="topLeft"
        getPopupContainer={() => document.body}
        zIndex={2000}
      >
        <span style={{ cursor: 'pointer' }}>{text}</span>
      </Popover>
    ),
  },
  {
    title: '错误码',
    key: 'errCode',
    width: 100,
    render:(_v,rcd)=>{
      const errorCode = rcd.errCode||rcd.smsCode;
      if(!errorCode)return '-';
      if(errorCode===SUCCESS)return '-';
      const errorMessage = getSmsErrorMessage(errorCode);
      const errorSolution = getSmsErrorSolution(errorCode);

      return (
        <Popover
          content={
            <div style={{ maxWidth: '400px' }}>
              <div><strong>错误信息：</strong></div>
              <div style={{ marginBottom: '8px' }}>{errorMessage}</div>
              {errorSolution && (
                <>
                  <div><strong>解决方案：</strong></div>
                  <div>{errorSolution}</div>
                </>
              )}
            </div>
          }
          title={errorCode ? `错误码：${errorCode}` : '错误信息'}
          trigger="click"
          placement="top"
          getPopupContainer={() => document.body}
          zIndex={2000}
        >
          <span style={{ cursor: 'pointer', color: '#1890ff' }}>
            {errorMessage}
          </span>
        </Popover>
      )
    }
  },
]

function OrderPage() {
  // 解密工具状态
  const [decryptorOpen, setDecryptorOpen] = useState(false)

  // 预览合同状态
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string>('')

  // 从URL初始化搜索参数
  const [searchParams, setSearchParams] = useState<OrderQueryParams>(() => {
    const search = new URLSearchParams(window.location.search)
    return {
      pageNum: search.get('pageNum') || '1',
      pageSize: search.get('pageSize') || '10',
      orderId: search.get('orderId') || undefined,
      userId: search.get('userId') || undefined,
      userName: search.get('userName') || undefined,
      userPhone: search.get('userPhone') || undefined,
      courseIds: search.getAll('courseIds').length
        ? search.getAll('courseIds')
        : [],
      positionCodeIds: search.getAll('positionCodeIds').length
        ? search.getAll('positionCodeIds')
        : [],
      classIds: search.getAll('classIds').length
        ? search.getAll('classIds')
        : [],
      regionIds: search.getAll('regionIds').length
        ? search.getAll('regionIds')
        : [],
      roomTypes: search.getAll('roomTypes').length
        ? search.getAll('roomTypes')
        : [],
      orderStatuses: search.getAll('orderStatuses').length
        ? search.getAll('orderStatuses')
        : [],
      agreementStatuses: search.getAll('agreementStatuses').length
        ? search.getAll('agreementStatuses')
        : [],
    }
  })

  // 同步URL参数
  useEffect(() => {
    const params = new URLSearchParams()

    // 添加分页参数
    params.set('pageNum', searchParams.pageNum)
    params.set('pageSize', searchParams.pageSize)

    // 添加搜索参数
    if (searchParams.orderId) params.set('orderId', searchParams.orderId)
    if (searchParams.userId) params.set('userId', searchParams.userId)
    if (searchParams.userName) params.set('userName', searchParams.userName)
    if (searchParams.userPhone) params.set('userPhone', searchParams.userPhone)

    // 添加多选参数
    searchParams.courseIds?.forEach((id) => params.append('courseIds', id))
    searchParams.positionCodeIds?.forEach((id) =>
      params.append('positionCodeIds', id),
    )
    searchParams.classIds?.forEach((id) => params.append('classIds', id))
    searchParams.regionIds?.forEach((id) => params.append('regionIds', id))
    searchParams.roomTypes?.forEach((type) => params.append('roomTypes', type))
    searchParams.orderStatuses?.forEach((status) =>
      params.append('orderStatuses', status),
    )
    searchParams.agreementStatuses?.forEach((status) =>
      params.append('agreementStatuses', status),
    )

    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [searchParams])

  // 使用 hooks 获取数据和操作方法
  const { data: orderData, isLoading } = useOrderList(searchParams)
  const createBlacklistMutation = useCreateBlacklist()
  const refundOrderMutation = useRefundOrder()
  const downloadAgreementMutation = useDownloadAgreement()
  const getAgreementEmbedUrlMutation = useGetAgreementEmbedUrl()
  const exportOrdersMutation = useExportOrders()

  // 未签署 参数（batchIds 可选，空即查询所有）
  const [unsignedParams, setUnsignedParams] = useState<{
    batchIds?: Array<number>
    pageNum: string
    pageSize: string
  }>(() => ({ pageNum: '1', pageSize: '10' }))
  const [unsignedModalOpen, setUnsignedModalOpen] = useState(false)
  const unsignedQuery = useUnsignedContractUsers(unsignedParams, {
    enabled: unsignedModalOpen,
  })

  // 短信发送 hooks（按考试项目 / 按订单）
  const sendByBatchMutation = useSendSignReminderByBatch()
  const sendByOrderIdsMutation = useSendSignReminderByOrderIds()

  // 短信详情查询状态
  const [smsDetailsModalOpen, setSmsDetailsModalOpen] = useState(false)
  const [smsDetailsParams, setSmsDetailsParams] = useState<SmsDetailsQueryParams>({
    pageNum: 1,
    pageSize: 10,
  })
  const smsDetailsQuery = useSmsDetails(smsDetailsParams)

  // 获取筛选选项数据
  const { data: coursesData } = useAllCourses()
  const { data: regionsData } = useAllRegions()
  const { data: positionsData } = usePositions({ pageNum: 1, pageSize: 10000 }) // 获取大量数据用于筛选
  const { data: classesData } = useClasses({ pageNum: 1, pageSize: 10000 }) // 获取大量数据用于筛选
  const { data: batchesData } = useBatches()

  const courses = coursesData || []
  const regions = regionsData || []
  const positions = positionsData?.records || []
  const classes = classesData?.records || []
  const batches = batchesData || []

  const getBatchNameById = (id: number) => {
    const matched = batches.find((b) => b.id === id)
    return matched?.name || '-'
  }

  // 未签署表格行选择（按订单批量发送短信）
  const [selectedOrderIds, setSelectedOrderIds] = useState<Array<string>>([])

  // 分页拉取所有未签署订单ID（可按考试项目筛选），并去重
  const fetchAllUnsignedOrderIds = async (
    batchIds?: Array<number>,
  ): Promise<Array<number>> => {
    const pageSizeNum = 1000
    let pageNumNum = 1
    let totalPagesNum = 1
    const collected: Array<number> = []

    do {
      const resp = await OrderService.getUnsignedContractUsers({
        pageNum: String(pageNumNum),
        pageSize: String(pageSizeNum),
        ...(batchIds && batchIds.length ? { batchIds } : {}),
      })
      const data = resp.data
      if (data?.records?.length) {
        for (const r of data.records) {
          const n = Number(r.orderId)
          if (Number.isFinite(n)) collected.push(n)
        }
      }
      totalPagesNum = data?.totalPages ?? pageNumNum
      pageNumNum += 1
    } while (pageNumNum <= totalPagesNum)

    return Array.from(new Set(collected))
  }

  const handleSendSmsByOrderIds = () => {
    if (!selectedOrderIds.length) {
      message.warning('请先选择要发送短信的订单')
      return
    }

    const orderIds: Array<number> = selectedOrderIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))

    if (!orderIds.length) {
      message.error('所选订单ID格式不正确')
      return
    }

    Modal.confirm({
      title: '确认发送短信',
      content: `将向 ${orderIds.length} 个订单发送签署提醒短信，是否继续？`,
      onOk: async () => {
        try {
          await sendByOrderIdsMutation.mutateAsync({ orderIds })
          message.success('开始发送短信，请稍后查看短信发送详情')
        } catch (err: unknown) {
          message.error((err as Error).message || '短信发送失败')
        }
      },
    })
  }

  const handleSendSmsByBatch = () => {
    const batchIds = unsignedParams.batchIds || []
    if (!batchIds.length) {
      message.warning('请先选择考试项目')
      return
    }

    const batchNames = batchIds.map((id) => getBatchNameById(id)).join('、')
    const allRecords = unsignedQuery.data?.records || []
    const affectedRecords = allRecords.filter((r) => batchIds.includes(r.batchId))
    const affectedTotal = unsignedQuery.data?.total ?? affectedRecords.length
    const previewList = affectedRecords
      .slice(0, 10)
      .map((r) => `${r.userName}(${r.userPhone})`)
      .join('、')

    Modal.confirm({
      title: '确认发送短信',
      content: (
        <div>
          <div>{`将按所选 ${batchIds.length} 个考试项目发送签署提醒短信，是否继续？`}</div>
          <div style={{ marginTop: 8 }}>{`考试项目：${batchNames || '-'}`}</div>
          <div style={{ marginTop: 8 }}>{`预计发送人数：${affectedTotal} 人`}</div>
          {affectedRecords.length > 0 && (
            <div style={{ marginTop: 8, color: '#666' }}>{`示例：${previewList}${
              affectedRecords.length > 10 ? ' 等' : ''
            }`}</div>
          )}
        </div>
      ),
      onOk: async () => {
        try {
          const allIds = await fetchAllUnsignedOrderIds(batchIds)
          if (!allIds.length) {
            message.info('未找到需要提醒的订单')
            return
          }
          await sendByOrderIdsMutation.mutateAsync({ orderIds: allIds })
          message.success('开始发送短信，请稍后查看短信发送详情')
        } catch (err: unknown) {
          message.error((err as Error).message || '短信发送失败')
        }
      },
    })
  }

  const handleSendSmsAllBatches = async () => {
    if (!batches.length) {
      message.warning('没有可用的考试项目')
      return
    }

    let totalOrders = 0
    try {
      const resp = await OrderService.getUnsignedContractUsers({
        pageNum: '1',
        pageSize: '1',
      })
      totalOrders = resp.data?.total ?? 0
    } catch (e) {}

    Modal.confirm({
      title: '确认一键批量提醒',
      content: (
        <div>
          <div>{`将向所有考试项目发送签署提醒短信，是否继续？`}</div>
          <div style={{ marginTop: 8 }}>{`考试项目数量：${batches.length} 个`}</div>
          <div style={{ marginTop: 8 }}>{`预计发送人数：${totalOrders} 人`}</div>
        </div>
      ),
      onOk: async () => {
        try {
          const allIds = await fetchAllUnsignedOrderIds()
          if (!allIds.length) {
            message.info('未找到需要提醒的订单')
            return
          }
          await sendByOrderIdsMutation.mutateAsync({ orderIds: allIds })
          message.success('已对所有考试项目发起短信提醒')
        } catch (err: unknown) {
          message.error((err as Error).message || '一键批量提醒失败')
        }
      },
    })
  }

  const handleBlacklist = (order: Order) => {
    Modal.confirm({
      title: '确认拉黑',
      content: `确定要将用户 ${order.userName} (${order.userPhone}) 加入黑名单吗？`,
      onOk: () => {
        createBlacklistMutation.mutate(
          {
            name: order.userName,
            phone: order.userPhone,
          },
          {
            onSuccess: () => {
              message.success('拉黑成功')
            },
            onError: (error: Error) => {
              message.error(error.message || '拉黑失败')
            },
          },
        )
      },
    })
  }

  const handleRefund = (order: Order) => {
    Modal.confirm({
      title: '确认退款',
      content: `确定要为订单 ${order.orderId} 申请退款吗？此操作不可撤销。`,
      onOk: () => {
        refundOrderMutation.mutate(order.orderId, {
          onSuccess: () => {
            message.success('退款申请成功')
          },
          onError: (error: Error) => {
            message.error(error.message || '退款申请失败')
          },
        })
      },
    })
  }

  const handleDownloadAgreement = (order: Order) => {
    downloadAgreementMutation.mutate(order.orderId, {
      onSuccess: (downloadUrl) => {
        // 在新标签页中打开下载链接
        window.open(downloadUrl, '_blank')
        message.success('合同下载链接已打开')
      },
      onError: (error: Error) => {
        message.error(error.message || '下载合同失败')
      },
    })
  }

  const handlePreviewAgreement = (order: Order) => {
    getAgreementEmbedUrlMutation.mutate(order.orderId, {
      onSuccess: (embedUrl) => {
        setPreviewUrl(embedUrl)
        setPreviewModalOpen(true)
      },
      onError: (error: Error) => {
        message.error(error.message || '获取合同预览链接失败')
      },
    })
  }

  const handlePreviewModalClose = () => {
    setPreviewModalOpen(false)
    setPreviewUrl('')
  }

  // 判断订单是否可以退款
  const canRefund = (orderStatus: string) => {
    const refundableStatuses = [
      'PENDING_CONTRACT',
      'PENDING_ACCOMMODATION',
      'PENDING_CLASS_ALLOCATION',
      'CLASS_ALLOCATED',
    ]
    return refundableStatuses.includes(orderStatus)
  }

  // 判断订单合同是否已生成
  const hasAgreement = (agreementStatus: string | null | undefined) => {
    return !!agreementStatus
  }

  const orders = orderData?.records || []
  const total = orderData?.total || 0
  const pageNum = orderData?.pageNum || 1
  const pageSize = orderData?.pageSize || 10

  // 搜索筛选组件
  const getSearchFilterDropdown =
    (key: keyof OrderQueryParams, placeholder: string) =>
    (props: FilterDropdownProps) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={placeholder}
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              const value = selectedKeys[0] as string
              setSearchParams((prev) => ({
                ...prev,
                [key]: value || undefined,
                pageNum: '1', // 重置到第一页
              }))
              confirm({ closeDropdown: true })
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                const value = selectedKeys[0] as string
                setSearchParams((prev) => ({
                  ...prev,
                  [key]: value || undefined,
                  pageNum: '1', // 重置到第一页
                }))
                confirm({ closeDropdown: true })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                setSearchParams((prev) => ({
                  ...prev,
                  [key]: undefined,
                  pageNum: '1', // 重置到第一页
                }))
                clearFilters && clearFilters()
                confirm({ closeDropdown: true })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </Space>
        </div>
      )
    }

  const columns: ColumnsType<Order> = [
    {
      title: '订单 ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 80,
      fixed: 'left',
      filteredValue: searchParams.orderId ? [searchParams.orderId] : undefined,
      filterDropdown: getSearchFilterDropdown('orderId', '搜索订单ID'),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '用户姓名',
      dataIndex: 'userName',
      key: 'userName',
      filteredValue: searchParams.userName
        ? [searchParams.userName]
        : undefined,
      filterDropdown: getSearchFilterDropdown('userName', '搜索用户姓名'),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '手机号',
      dataIndex: 'userPhone',
      key: 'userPhone',
      filteredValue: searchParams.userPhone
        ? [searchParams.userPhone]
        : undefined,
      filterDropdown: getSearchFilterDropdown('userPhone', '搜索手机号'),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '课程名称',
      dataIndex: 'courseName',
      key: 'courseName',
      filters: Array.isArray(courses)
        ? courses.map((course: any) => ({
            text: course.name,
            value: course.id.toString(),
          }))
        : [],
      filteredValue: searchParams.courseIds || undefined,
      render: (text: string) => (
        <Link to="/course" search={{ name: text }}>
          {text}
        </Link>
      ),
    },
    {
      title: '职位代码',
      dataIndex: 'positionCodeName',
      key: 'positionCodeName',
      filters: Array.isArray(positions)
        ? positions.map((position: any) => ({
            text: position.name,
            value: position.id.toString(),
          }))
        : [],
      filteredValue: searchParams.positionCodeIds || undefined,
      render: (positionCodeName: string) => {
        const formattedName = formatPositionCodeName(positionCodeName)
        return (
          <Link to="/positionCode" search={{ name: positionCodeName }}>
            {formattedName}
          </Link>
        )
      },
    },
    {
      title: '考试成绩',
      dataIndex: 'examScore',
      key: 'examScore',
    },
    {
      title: '是否状元',
      dataIndex: 'isZhuangyuan',
      key: 'isZhuangyuan',
      render: (isZhuangyuan: boolean) => (
        <Tag color={isZhuangyuan ? 'gold' : 'default'}>
          {isZhuangyuan ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '性别',
      dataIndex: 'userGender',
      key: 'userGender',
      render: (gender: string) => (
        <Tag color={gender === '男' ? 'blue' : 'pink'}>{gender}</Tag>
      ),
    },
    {
      title: '住宿',
      dataIndex: 'accommodationType',
      key: 'accommodationType',
      render: (accommodationType: string | undefined) => {
        if (!accommodationType) return '-'
        const accommodationMap: Record<string, string> = {
          single: '单人间',
          double: '双人间',
        }
        return accommodationMap[accommodationType] || accommodationType
      },
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      filters: ORDER_STATUS_OPTIONS,
      filteredValue: searchParams.orderStatuses || undefined,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          PENDING_PAYMENT: { color: 'gray', text: '待支付' },
          PENDING_CONTRACT: { color: 'orange', text: '待签约' },
          PENDING_ACCOMMODATION: { color: 'blue', text: '待选择住宿' },
          PENDING_CLASS_ALLOCATION: { color: 'purple', text: '待分配班级' },
          CLASS_ALLOCATED: { color: 'green', text: '已分配班级' },
          CANCELLED: { color: 'red', text: '已取消' },
          REFUNDED: { color: 'red', text: '已退款' },
        }
        const statusInfo = statusMap[status] || {
          color: 'default',
          text: status,
        }
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
      },
    },
    {
      title: '班级',
      dataIndex: 'className',
      key: 'className',
      filters: Array.isArray(classes)
        ? classes.map((cls: any) => ({
            text: cls.name,
            value: cls.id.toString(),
          }))
        : [],
      filteredValue: searchParams.classIds || undefined,
      render: (className: string | null) => {
        if (!className) return '-'
        return (
          <Link to="/class" search={{ name: className }}>
            {className}
          </Link>
        )
      },
    },
    {
      title: '地区',
      dataIndex: 'regionName',
      key: 'regionName',
      filters: Array.isArray(regions)
        ? regions.map((region: any) => ({
            text: region.name,
            value: region.id.toString(),
          }))
        : [],
      filteredValue: searchParams.regionIds || undefined,
      render: (regionName: string) => <Link to="/region">{regionName}</Link>,
    },
    {
      title: '协议类型',
      dataIndex: 'agreementType',
      key: 'agreementType',
      render: (type: string) => {
        const typeMap: Record<string, { color: string; text: string }> = {
          ZHUANGYUAN: { color: 'gold', text: '状元协议' },
          NONZHUANGYUAN: { color: 'blue', text: '非状元协议' },
          NONAGREEMENT: { color: 'default', text: '非协议' },
        }
        const typeInfo = typeMap[type] || { color: 'default', text: type }
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>
      },
    },
    {
      title: '协议状态',
      dataIndex: 'agreementStatus',
      key: 'agreementStatus',
      filters: AGREEMENT_STATUS_OPTIONS,
      filteredValue: searchParams.agreementStatuses || undefined,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          PENDING_SIGN: { color: 'orange', text: '待签署' },
          SIGNED: { color: 'green', text: '已签署' },
        }
        const statusInfo = statusMap[status] || {
          color: 'default',
          text: status,
        }
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDateTime,
      sorter: (a, b) => {
        if (!a.createdAt || !b.createdAt) return 0
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      },
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: formatDateTime,
      sorter: (a, b) => {
        if (!a.updatedAt || !b.updatedAt) return 0
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => handleRefund(record)}
            loading={refundOrderMutation.isPending}
            disabled={!canRefund(record.orderStatus)}
          >
            退款
          </Button>

          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handlePreviewAgreement(record)}
            loading={getAgreementEmbedUrlMutation.isPending}
            disabled={!hasAgreement(record.agreementStatus)}
            title={
              !hasAgreement(record.agreementStatus) ? '合同尚未生成' : ''
            }
          >
            预览合同
          </Button>

          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadAgreement(record)}
            loading={downloadAgreementMutation.isPending}
            disabled={!hasAgreement(record.agreementStatus)}
            title={
              !hasAgreement(record.agreementStatus) ? '合同尚未生成' : ''
            }
          >
            下载合同
          </Button>

          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBlacklist(record)}
          >
            拉黑
          </Button>
        </Space>
      ),
    },
  ]

  // Excel导出处理函数
  const handleExcelExport = (exportAll: boolean) => {
    const exportParams = exportAll
      ? {
          pageNum: '1',
          pageSize: '10000', // 导出所有数据时使用大页码
        }
      : searchParams // 导出当前筛选结果时使用当前参数

    exportOrdersMutation.mutate(exportParams, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `orders_${new Date().toISOString().slice(0, 10)}.xlsx`,
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        message.success(exportAll ? '全部订单导出成功' : '当前筛选结果导出成功')
      },
      onError: (error) => {
        message.error(`导出失败: ${error.message}`)
      },
    })
  }

  // 处理表格筛选变化
  const handleTableChange = (pagination: any, filters: any) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNum: pagination.current,
      pageSize: pagination.pageSize,
      courseIds: filters.courseName || [],
      positionCodeIds: filters.positionCodeName || [],
      classIds: filters.className || [],
      regionIds: filters.regionName || [],
      orderStatuses: filters.orderStatus || [],
      agreementStatuses: filters.agreementStatus || [],
    }))
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">订单管理</h2>
        <Space>
          <Button onClick={() => setUnsignedModalOpen(true)}>未签约短信提醒</Button>
          <Button onClick={() => setSmsDetailsModalOpen(true)}>短信详情</Button>
          <Button
            icon={<KeyOutlined />}
            onClick={() => setDecryptorOpen(true)}
          >
            订单号解密
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'current',
                  label: '导出当前筛选结果',
                  icon: <FileExcelOutlined />,
                  onClick: () => handleExcelExport(false),
                },
                {
                  key: 'all',
                  label: '导出全部订单',
                  icon: <FileExcelOutlined />,
                  onClick: () => handleExcelExport(true),
                },
              ] as MenuProps['items'],
            }}
            disabled={exportOrdersMutation.isPending}
          >
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={exportOrdersMutation.isPending}
            >
              导出 Excel
            </Button>
          </Dropdown>
        </Space>
      </div>

      <Spin spinning={isLoading}>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="orderId"
          bordered
          size="middle"
          pagination={getPagination(pageNum, pageSize, total)}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
          sticky={{ offsetScroll: 0 }}
        />
      </Spin>

      <OrderDecryptor
        open={decryptorOpen}
        onClose={() => setDecryptorOpen(false)}
      />

      <Modal
        title="预览合同"
        open={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={null}
        width="90%"
        style={{ maxWidth: 1200 }}
        destroyOnHidden
        centered
      >
        <div style={{ height: '80vh' }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="合同预览"
            />
          )}
        </div>
      </Modal>

      <Modal
        title="短信提醒"
        open={unsignedModalOpen}
        onCancel={() => {
          setUnsignedModalOpen(false)
          setSelectedOrderIds([])
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Tooltip title="对你在表格中勾选的订单发送提醒（可多选）">
              <Button
                onClick={handleSendSmsByOrderIds}
                loading={sendByOrderIdsMutation.isPending}
                disabled={selectedOrderIds.length === 0}
              >
                单独提醒（可多选）
                <QuestionCircleOutlined style={{ marginLeft: 6 }} />
              </Button>
            </Tooltip>
            <Tooltip title="对所选考试项目下的全部未签署用户发送提醒（可多选）">
              <Button
                type="primary"
                onClick={handleSendSmsByBatch}
                loading={sendByBatchMutation.isPending}
                disabled={!(unsignedParams.batchIds && unsignedParams.batchIds.length)}
              >
                考试项目批量提醒
                <QuestionCircleOutlined style={{ marginLeft: 6 }} />
              </Button>
            </Tooltip>
          </div>
        }
        width="90%"
        style={{ maxWidth: 1400 }}
        destroyOnHidden
        centered
      >
        <div style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 12 }}>
            <Space>
              <span>筛选考试项目：</span>
              <Select
                mode="multiple"
                style={{ minWidth: 320 }}
                placeholder="不选则查询所有考试项目"
                value={unsignedParams.batchIds}
                onChange={(values: number[]) =>
                  setUnsignedParams((prev) => ({
                    ...prev,
                    batchIds: values.length ? values : undefined,
                    pageNum: '1',
                  }))
                }
                options={batches.map((b) => ({ label: b.name, value: b.id }))}
              />
            </Space>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <Spin spinning={unsignedQuery.isLoading}>
              <Table<UnsignedContractUser>
                columns={[
                  { title: '订单ID', dataIndex: 'orderId', key: 'orderId', width: 120 },
                  { title: '用户姓名', dataIndex: 'userName', key: 'userName' },
                  { title: '手机号', dataIndex: 'userPhone', key: 'userPhone' },
                  {
                    title: '考试项目',
                    dataIndex: 'batchId',
                    key: 'batchName',
                    width: 160,
                    render: (batchId: number) => getBatchNameById(batchId),
                  },
                ]}
                dataSource={unsignedQuery.data?.records || []}
                rowKey={(r) => `${r.orderId}`}
                rowSelection={{
                  selectedRowKeys: selectedOrderIds,
                  onChange: (keys) => setSelectedOrderIds(keys as Array<string>),
                }}
                bordered
                size="middle"
                pagination={getPagination(
                  unsignedQuery.data?.pageNum || 1,
                  unsignedQuery.data?.pageSize || 10,
                  unsignedQuery.data?.total || 0,
                  (p, ps) =>
                    setUnsignedParams((prev) => ({
                      ...prev,
                      pageNum: String(p),
                      pageSize: String(ps),
                    })),
                )}
                scroll={{ y: 'calc(80vh - 140px)' }}
              />
            </Spin>
          </div>
        </div>
      </Modal>

      <Modal
        title="短信详情"
        open={smsDetailsModalOpen}
        onCancel={() => {
          setSmsDetailsModalOpen(false)
          setSmsDetailsParams({ pageNum: 1, pageSize: 10 })
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: 1400 }}
        centered
      >
        <div style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 12 }}>
            <Space>
              <span>开始时间：</span>
              <DatePicker
                style={{ width: 150 }}
                placeholder="选择开始时间"
                format={DATE_TIME_FORMAT}
                showTime
                onChange={(date) => {
                  setSmsDetailsParams(prev => ({
                    ...prev,
                    startTime: date ? dayjs(date).format(DATE_TIME_FORMAT) : undefined,
                    pageNum: 1,
                  }))
                }}
              />
              <span>结束时间：</span>
              <DatePicker
                style={{ width: 150 }}
                placeholder="选择结束时间"
                format={DATE_TIME_FORMAT}
                showTime
                onChange={(date) => {
                  setSmsDetailsParams(prev => ({
                    ...prev,
                    endTime: date ? dayjs(date).format(DATE_TIME_FORMAT) : undefined,
                    pageNum: 1,
                  }))
                }}
              />
              <span>手机号：</span>
              <Input
                style={{ width: 150 }}
                placeholder="搜索手机号"
                onPressEnter={(e) => {
                  const value = (e.target as HTMLInputElement).value
                  setSmsDetailsParams(prev => ({
                    ...prev,
                    phone: value || undefined,
                    pageNum: 1,
                  }))
                }}
              />
              <span>短信状态：</span>
              <Select
                mode="multiple"
                style={{ width: 200 }}
                placeholder="选择短信状态"
                allowClear
                value={smsDetailsParams.status}
                onChange={(values) => {
                  setSmsDetailsParams(prev => ({
                    ...prev,
                    status: values.length ? values : undefined,
                    pageNum: 1,
                  }))
                }}
              >
                {Object.entries(SMS_STATUS_MAP).map(([value, info]) => (
                  <Select.Option key={value} value={value}>
                    {info.text}
                  </Select.Option>
                ))}
              </Select>
              <Button
                onClick={() => setSmsDetailsParams({ pageNum: 1, pageSize: 10 })}
              >
                重置
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => smsDetailsQuery.refetch()}
                loading={smsDetailsQuery.isLoading}
              >
                刷新
              </Button>
            </Space>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <Spin spinning={smsDetailsQuery.isLoading}>
              <Table
                columns={smsDetailsColumns}
                dataSource={smsDetailsQuery.data?.records || []}
                rowKey="id"
                bordered
                size="middle"
                pagination={getPagination(
                  smsDetailsQuery.data?.pageNum || 1,
                  smsDetailsQuery.data?.pageSize || 10,
                  smsDetailsQuery.data?.total || 0,
                  (p, ps) =>
                    setSmsDetailsParams((prev) => ({
                      ...prev,
                      pageNum: p,
                      pageSize: ps,
                    })),
                )}
                scroll={{ y: TABLE_SCROLL_HEIGHT, x: TABLE_SCROLL_X }}
              />
            </Spin>
          </div>
        </div>
      </Modal>
    </div>
  )
}
